import { useEffect, useRef, useState } from 'react';
import {
  MessageSquare,
  Trash2,
  Loader2,
  FileText,
  AlertCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useChatMessages, useSendMessage, useClearChat } from '@/features/chat/hooks/useChat';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';

// =========================================
// ChatPanel — full chat interface
// =========================================

interface ChatPanelProps {
  sourceId: string;
  sourceName?: string;
  className?: string;
  highlightedPassage?: string;
  onCitationClick?: (passage: { text: string; relevance: string }) => void;
}

export function ChatPanel({ sourceId, sourceName, className, highlightedPassage, onCitationClick }: ChatPanelProps) {
  const [aiModel, setAiModel] = useState<'gpt-4-turbo' | 'claude-3.5-sonnet'>(
    'gpt-4-turbo'
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const {
    data: messages = [],
    isLoading: loadingMessages,
    error: messagesError,
  } = useChatMessages(sourceId);

  const sendMessage = useSendMessage();
  const clearChat = useClearChat();

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, sendMessage.isPending]);

  const handleSend = (message: string) => {
    sendMessage.mutate({
      source_id: sourceId,
      message,
      ai_model: aiModel,
    });
  };

  const handleClear = () => {
    clearChat.mutate(sourceId);
  };

  return (
    <div
      className={cn(
        'flex flex-col h-full border rounded-xl bg-white overflow-hidden',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50/50">
        <div className="flex items-center gap-2 min-w-0">
          <MessageSquare className="h-4 w-4 text-indigo-600 shrink-0" />
          <h3 className="font-semibold text-sm text-slate-800 truncate">
            Chat
          </h3>
          {sourceName && (
            <Badge variant="outline" className="text-[10px] truncate max-w-[180px]">
              <FileText className="h-3 w-3 mr-1 shrink-0" />
              {sourceName}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Select
            value={aiModel}
            onValueChange={(v) =>
              setAiModel(v as 'gpt-4-turbo' | 'claude-3.5-sonnet')
            }
          >
            <SelectTrigger className="h-7 w-[140px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="gpt-4-turbo">GPT-4 Turbo</SelectItem>
              <SelectItem value="claude-3.5-sonnet">Claude 3.5</SelectItem>
            </SelectContent>
          </Select>

          <AlertDialog>
            <Tooltip>
              <TooltipTrigger asChild>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    disabled={messages.length === 0 || clearChat.isPending}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-slate-400" />
                  </Button>
                </AlertDialogTrigger>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">Clear chat history</p>
              </TooltipContent>
            </Tooltip>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Clear chat history?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete all messages in this conversation.
                  This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleClear}
                  className="bg-rose-600 hover:bg-rose-700"
                >
                  Clear history
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loadingMessages ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-6 w-6 animate-spin text-slate-300" />
          </div>
        ) : messagesError ? (
          <div className="flex flex-col items-center justify-center h-full gap-2 text-slate-400">
            <AlertCircle className="h-8 w-8" />
            <p className="text-sm">Failed to load messages</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-slate-400">
            <MessageSquare className="h-10 w-10 stroke-[1.2]" />
            <div className="text-center">
              <p className="text-sm font-medium text-slate-500">
                Start a conversation
              </p>
              <p className="text-xs mt-1">
                Ask questions about this document and get answers with cited
                passages.
              </p>
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg) => (
              <ChatMessage
                key={msg.id}
                message={msg}
                highlightedPassage={highlightedPassage}
                onCitationClick={onCitationClick}
              />
            ))}
            {sendMessage.isPending && (
              <div className="flex gap-3">
                <div className="flex items-center justify-center h-8 w-8 rounded-full bg-slate-100 shrink-0">
                  <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                </div>
                <div className="bg-slate-100 rounded-2xl rounded-tl-sm px-4 py-2.5">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-slate-300 rounded-full animate-bounce [animation-delay:0ms]" />
                    <span className="w-2 h-2 bg-slate-300 rounded-full animate-bounce [animation-delay:150ms]" />
                    <span className="w-2 h-2 bg-slate-300 rounded-full animate-bounce [animation-delay:300ms]" />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <ChatInput
        onSend={handleSend}
        isLoading={sendMessage.isPending}
        disabled={!sourceId}
      />
    </div>
  );
}
